import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'

import { Button } from '@/common/ui/button'
import { SortingArrows } from '@/common/components/SortingArrows'
import { Badge } from '@/common/ui/badge'
import { formatBankName } from '@/modules/bank/utils/formatBankName'

import type { OperatorDepositTransaction } from '@/modules/bank/type'

const STATUS_COLOR: Record<string, string> = {
	CREATED: 'text-blue-500',
	PENDING: 'text-yellow-500',
	PROCESSING: 'text-amber-500',
	COMPLETED: 'text-green-500',
	FAILED: 'text-red-500',
	CANCELED: 'text-red-500',
	EXPIRED: 'text-gray-500',
	MANUAL_COMPLETED: 'text-green-500',
	PAYMENT_GATEWAY_FAILED: 'text-red-500',
}
const STATUS_LABEL: Record<string, string> = {
	CREATED: 'สร้างแล้ว',
	PENDING: 'รอดำเนินการ',
	PROCESSING: 'กำลังตรวจสอบ',
	COMPLETED: 'สำเร็จ',
	FAILED: 'ไม่สำเร็จ',
	CANCELED: 'ยกเลิก',
	EXPIRED: 'หมดอายุ',
	MANUAL_COMPLETED: 'รอตรวจสอบ',
	PAYMENT_GATEWAY_FAILED: 'ล้มเหลว',
}

export const columns: ColumnDef<OperatorDepositTransaction>[] = [
	{
		accessorKey: 'transactionInfo',
		header: 'ข้อมูลธุรกรรม',
		cell: ({ row }) => {
			const txId = String(row.original.txId || row.original.id)
			const name = row.original.paymentGatewayName
			return (
				<div className="space-y-1 text-sm">
					<div
						className="max-w-[220px] truncate font-mono text-xs"
						title={txId}
					>
						{txId}
					</div>
					{name ? (
						<Badge variant="outline">{name}</Badge>
					) : (
						<span className="text-muted-foreground">-</span>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'operatorBankInfo',
		header: 'ผู้ทำรายการ/บัญชี',
		cell: ({ row }) => {
			const username = row.original.operatorUsername
			return (
				<div className="space-y-1 text-sm">
					<p>{username || '-'}</p>
					<p className="text-xs text-muted-foreground">
						{formatBankName(row.original.bankProvider)}
					</p>
					<p className="text-xs text-muted-foreground">
						{row.original.accountNumber || '-'}
					</p>
					<p className="text-xs text-muted-foreground">
						{row.original.accountName || '-'}
					</p>
				</div>
			)
		},
	},
	{
		accessorKey: 'amount',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				className="px-0 hover:bg-transparent"
			>
				จำนวนเงิน
				<SortingArrows sortOrder={column.getIsSorted()} />
			</Button>
		),
		cell: ({ row }) => `฿${row.original.amount.toLocaleString('th-TH')}`,
	},
	{
		accessorKey: 'createdAt',
		header: 'สถานะ/เวลา',
		cell: ({ row }) => {
			const status = row.original.status
			return (
				<div className="space-y-1 text-sm">
					<span className={STATUS_COLOR[status] || 'text-gray-500'}>
						{STATUS_LABEL[status] || status}
					</span>
					<p className="text-xs text-muted-foreground">
						{format(new Date(row.original.createdAt || ''), 'PPP HH:mm น.', {
							locale: th,
						})}
					</p>
				</div>
			)
		},
	},
]
